import * as React from "react";
import { useEffect } from "react";
import { RootState } from "../reducers/index";
import { useAppSelector } from "../store/index";
import { IInitOptions } from "../interfaces/initoptions";
import { SubmitHandler, useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { useSendLeadMutation } from "../reducers/facilityapi";
import { sendFacilityLead } from "../api/googleanalytics";

type Inputs = {
    name: string;
    email: string;
    phone?: string;
    message: string;
};

interface ILeadFormProps {
    onSubmitted(): void;
}

const LeadForm = ({ onSubmitted }: ILeadFormProps) => {
    const { facilityId, facilityName } = useAppSelector<IInitOptions>((
        state: RootState,
    ) => state.initOptions);
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isSubmitting, isSubmitSuccessful },
    } = useForm<Inputs>();
    const [postLead, { isSuccess }] = useSendLeadMutation();

    const onSubmit: SubmitHandler<Inputs> = (data: Inputs) => {
        const submitData = {
            facilityId: facilityId,
            name: data.name,
            email: data.email,
            phone: data.phone,
            message: data.message,
        };
        //console.log(submitData);
        postLead(submitData);

        // generate GA event
        sendFacilityLead();
    };

    useEffect(() => {
        if (isSuccess) {
            onSubmitted();
        }
    }, [isSuccess]);

    return (
        /* "handleSubmit" will validate your inputs before invoking "onSubmit" */
        <>
            <p>
                Use this form to contact{" "}
                {facilityName}. We will forward this to information to them and
                they will contact you directly:
            </p>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="form-floating mb-3">
                    <input
                        type="text"
                        className={errors.name
                            ? "form-control is-invalid"
                            : "form-control"}
                        placeholder="Your name"
                        id="name"
                        {...register("name", {
                            required: "Please provide your name",
                        })}
                    />
                    <label htmlFor="name">Your name</label>
                    <ErrorMessage
                        errors={errors}
                        name="name"
                        render={({ message }) => (
                            <div className="form-text text-danger">
                                {message}
                            </div>
                        )}
                    />
                </div>
                <div className="form-floating mb-3">
                    <input
                        type="email"
                        className={errors.email
                            ? "form-control is-invalid"
                            : "form-control"}
                        placeholder="Your email"
                        id="email"
                        {...register("email", {
                            required: "Please provide your email",
                        })}
                    />
                    <label htmlFor="email">Your email address</label>
                    <ErrorMessage
                        errors={errors}
                        name="email"
                        render={({ message }) => (
                            <div className="form-text text-danger">
                                {message}
                            </div>
                        )}
                    />
                </div>
                <div className="form-floating mb-3">
                    <input
                        type="tel"
                        className={errors.phone
                            ? "form-control is-invalid"
                            : "form-control"}
                        placeholder="Your phone"
                        id="phone"
                        {...register("phone")}
                    />
                    <label htmlFor="phone">Your phone</label>
                    <ErrorMessage
                        errors={errors}
                        name="phone"
                        render={({ message }) => (
                            <div className="form-text text-danger">
                                {message}
                            </div>
                        )}
                    />
                </div>
                <div className="form-floating mb-3">
                    <textarea
                        className={errors.message
                            ? "form-control form-control-message is-invalid"
                            : "form-control form-control-message"}
                        placeholder="Comments or Questions"
                        id="message"
                        {...register("message", {
                            required:
                                "Please provide your comments or questions",
                        })}
                    >
                    </textarea>
                    <label htmlFor="message">Comments or Questions</label>
                    <ErrorMessage
                        errors={errors}
                        name="message"
                        render={({ message }) => (
                            <div className="form-text text-danger">
                                {message}
                            </div>
                        )}
                    />
                </div>
                <div className="mb-3">
                    <input
                        type="submit"
                        value="Send Email"
                        className="btn btn-primary"
                        disabled={isSubmitting || isSubmitSuccessful}
                    />
                </div>
            </form>
            <p className="form-disclaimer">
                Please note that email communication may not be entirely secure.
                Sending an email through this page does not guarantee that the
                recipient will receive, read, or respond to it and spam filters
                could prevent its delivery.
            </p>
        </>
    );
};

export default LeadForm;
